'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { PowerBarMarker } from '@/domains/pages/shared/power-bar-marker';
import { PowerBarDotMarker } from '@/domains/pages/shared/power-bar-dot-marker';
import { POWER_BAR_COMPARISON_MARKER_COLOR, POWER_BAR_COMPARISON_MARKER_COLOR_LIT, POWER_BAR_DOT_GAP_PX, POWER_BAR_MARKER_LINE_WIDTH, POWER_BAR_TICK_COLOR, POWER_BAR_TRACK_CLASS, powerBarMarkerPosition, powerBarScoreFillStyle, selectedValueTone, STANDARD_POWER_BAR_LAYOUT } from '@/domains/pages/shared/power-bar-shared';
import { AIFI_NEGATIVE_COLOR, AIFI_POSITIVE_TEXT_COLOR } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import type { SegmentSummary } from '@/domains/pages/ai-proficiency/utils/map-segment-aggregates';
import { cn } from '@/utils/shad';
import { formatScore } from '@/utils/format.util';
import { useCallback, useRef, useState } from 'react';
type SegmentPowerBarHoverTarget = 'bar' | 'median' | 'best';
type SegmentPowerBarProps = {
  selectedName: string;
  selectedDisplayName: string;
  selectedScore: number | null;
  median: number;
  bestScore: number;
  bestName: string;
  peers: SegmentSummary[];
  segmentNoun: string;
  className?: string;
};
function markerPosition(value: number): string {
  return powerBarMarkerPosition(value);
}
export function SegmentPowerBar(t0) {
  const $ = _c(89);
  const {
    selectedName,
    selectedDisplayName,
    selectedScore,
    median,
    bestScore,
    bestName,
    segmentNoun,
    className
  } = t0;
  const barRef = useRef(null);
  const [hoverTarget, setHoverTarget] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 0,
      y: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tooltipPos, setTooltipPos] = useState(t1);
  const score = selectedScore ?? 0;
  let t2;
  if ($[1] !== score) {
    t2 = markerPosition(score);
    $[1] = score;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const scoreFill = t2;
  const vsMedian = score - median;
  const vsBest = score - bestScore;
  const aheadOfMedian = vsMedian >= 0;
  const aheadOfBest = vsBest >= 0;
  const selectedIsBest = selectedName === bestName;
  const barTopPx = STANDARD_POWER_BAR_LAYOUT.trackTopPx;
  const dotTopPx = barTopPx + STANDARD_POWER_BAR_LAYOUT.trackHeightPx + POWER_BAR_DOT_GAP_PX;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ratio => {
      const bar = barRef.current;
      if (!bar) {
        return;
      }
      const rect = bar.getBoundingClientRect();
      setTooltipPos({
        x: rect.left + rect.width * Math.min(1, Math.max(0, ratio)),
        y: rect.top
      });
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const updateTooltipAtRatio = t3;
  let t4;
  if ($[4] !== score) {
    t4 = () => {
      updateTooltipAtRatio(score / 100);
      setHoverTarget("bar");
    };
    $[4] = score;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const showBarTooltip = t4;
  let t5;
  if ($[6] !== median) {
    t5 = () => {
      updateTooltipAtRatio(median / 100);
      setHoverTarget("median");
    };
    $[6] = median;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const showMedianTooltip = t5;
  let t6;
  if ($[8] !== bestScore) {
    t6 = () => {
      updateTooltipAtRatio(bestScore / 100);
      setHoverTarget("best");
    };
    $[8] = bestScore;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const showBestTooltip = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setHoverTarget(null);
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const hideTooltip = t7;
  const barLit = hoverTarget === "bar";
  const medianLit = hoverTarget === "median";
  const bestLit = hoverTarget === "best";
  let t8;
  if ($[11] !== aheadOfBest || $[12] !== aheadOfMedian || $[13] !== bestScore || $[14] !== hoverTarget || $[15] !== median || $[16] !== score || $[17] !== segmentNoun || $[18] !== selectedDisplayName || $[19] !== vsBest || $[20] !== vsMedian) {
    t8 = hoverTarget === "bar" ? [{
      label: selectedDisplayName,
      value: formatScore(score, {
        decimals: 1
      }),
      valueClassName: "text-foreground"
    }, {
      label: `Median ${segmentNoun}`,
      value: formatScore(median, {
        decimals: 1
      })
    }, {
      label: `Best ${segmentNoun}`,
      value: formatScore(bestScore, {
        decimals: 1
      })
    }, {
      label: "vs. median",
      value: `${aheadOfMedian ? "+" : ""}${formatScore(vsMedian, {
        decimals: 1
      })}`,
      valueStyle: {
        color: aheadOfMedian ? AIFI_POSITIVE_TEXT_COLOR : AIFI_NEGATIVE_COLOR
      }
    }, {
      label: `vs. best ${segmentNoun}`,
      value: `${aheadOfBest ? "+" : ""}${formatScore(vsBest, {
        decimals: 1
      })}`,
      valueStyle: {
        color: aheadOfBest ? AIFI_POSITIVE_TEXT_COLOR : AIFI_NEGATIVE_COLOR
      }
    }] : hoverTarget === "median" ? [{
      label: `Median ${segmentNoun}`,
      value: formatScore(median, {
        decimals: 1
      })
    }, {
      label: selectedDisplayName,
      value: formatScore(score, {
        decimals: 1
      }),
      ...selectedValueTone(aheadOfMedian)
    }, {
      label: "Difference",
      value: `${aheadOfMedian ? "+" : ""}${formatScore(vsMedian, {
        decimals: 1
      })}`,
      ...selectedValueTone(aheadOfMedian)
    }] : hoverTarget === "best" ? [{
      label: `Best ${segmentNoun}`,
      value: formatScore(bestScore, {
        decimals: 1
      })
    }, {
      label: selectedDisplayName,
      value: formatScore(score, {
        decimals: 1
      }),
      ...selectedValueTone(aheadOfBest)
    }, {
      label: "Difference",
      value: `${aheadOfBest ? "+" : ""}${formatScore(vsBest, {
        decimals: 1
      })}`,
      ...selectedValueTone(aheadOfBest)
    }] : [];
    $[11] = aheadOfBest;
    $[12] = aheadOfMedian;
    $[13] = bestScore;
    $[14] = hoverTarget;
    $[15] = median;
    $[16] = score;
    $[17] = segmentNoun;
    $[18] = selectedDisplayName;
    $[19] = vsBest;
    $[20] = vsMedian;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const tooltipRows = t8;
  let t9;
  if ($[22] !== className) {
    t9 = cn("relative", className);
    $[22] = className;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = barLit && "brightness-110";
  let t11;
  if ($[24] !== t10) {
    t11 = cn("absolute right-0 left-0 cursor-pointer transition-[filter] duration-150", t10);
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      top: barTopPx,
      height: `${STANDARD_POWER_BAR_LAYOUT.trackHeightPx}px`
    };
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = barLit && "brightness-105";
  let t14;
  if ($[27] !== t13) {
    t14 = cn("absolute inset-0 transition-[filter] duration-150", POWER_BAR_TRACK_CLASS, t13);
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t14) {
    t15 = <div className={t14} />;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = barLit && "brightness-105";
  let t17;
  if ($[31] !== t16) {
    t17 = cn("absolute inset-y-0 left-0 transition-[filter] duration-150", t16);
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== score) {
    t18 = powerBarScoreFillStyle(score);
    $[33] = score;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== scoreFill || $[36] !== t18) {
    t19 = {
      width: scoreFill,
      ...t18
    };
    $[35] = scoreFill;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t17 || $[39] !== t19) {
    t20 = <div className={t17} style={t19} />;
    $[38] = t17;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== showBarTooltip || $[42] !== t11 || $[43] !== t15 || $[44] !== t20) {
    t21 = <div className={t11} style={t12} onMouseEnter={showBarTooltip}>{t15}{t20}</div>;
    $[41] = showBarTooltip;
    $[42] = t11;
    $[43] = t15;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== score || $[47] !== selectedIsBest) {
    t22 = !selectedIsBest && score > 0 ? <span className="absolute z-[6] w-px" style={{
      left: markerPosition(score),
      top: barTopPx,
      height: `${STANDARD_POWER_BAR_LAYOUT.trackHeightPx}px`,
      backgroundColor: POWER_BAR_TICK_COLOR
    }} /> : null;
    $[46] = score;
    $[47] = selectedIsBest;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== median) {
    t23 = markerPosition(median);
    $[49] = median;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== medianLit || $[52] !== showMedianTooltip || $[53] !== t23) {
    t24 = <PowerBarMarker layout={STANDARD_POWER_BAR_LAYOUT} position={t23} lit={medianLit} color={POWER_BAR_COMPARISON_MARKER_COLOR} litColor={POWER_BAR_COMPARISON_MARKER_COLOR_LIT} ariaLabel="Median marker" onMouseEnter={showMedianTooltip} onBlur={hideTooltip} />;
    $[51] = medianLit;
    $[52] = showMedianTooltip;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== bestScore) {
    t25 = markerPosition(bestScore);
    $[55] = bestScore;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== bestLit || $[58] !== showBestTooltip || $[59] !== t25) {
    t26 = <PowerBarDotMarker layout={STANDARD_POWER_BAR_LAYOUT} position={t25} lit={bestLit} color={POWER_BAR_COMPARISON_MARKER_COLOR} litColor={POWER_BAR_COMPARISON_MARKER_COLOR_LIT} ariaLabel="Best marker" onMouseEnter={showBestTooltip} onFocus={showBestTooltip} onBlur={hideTooltip} />;
    $[57] = bestLit;
    $[58] = showBestTooltip;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div style={{
      height: dotTopPx + 4
    }} />;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t21 || $[63] !== t22 || $[64] !== t24 || $[65] !== t26) {
    t28 = <div ref={barRef} className="relative" onMouseLeave={hideTooltip}>{t21}{t22}{t24}{t26}{t27}</div>;
    $[62] = t21;
    $[63] = t22;
    $[64] = t24;
    $[65] = t26;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className={cn("flex justify-between text-[10px] leading-none text-muted-foreground", AIFI_DATA_NUMBER_CLASS)}><span>0</span><span>100</span></div>;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <span className="h-3 shrink-0 rounded-full" style={{
      width: POWER_BAR_MARKER_LINE_WIDTH,
      backgroundColor: POWER_BAR_COMPARISON_MARKER_COLOR
    }} />;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== segmentNoun) {
    t31 = <span className="flex items-center gap-2">{t30}Median {segmentNoun}</span>;
    $[69] = segmentNoun;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <span className="size-1.5 shrink-0 rounded-full" style={{
      backgroundColor: POWER_BAR_COMPARISON_MARKER_COLOR
    }} />;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== segmentNoun) {
    t33 = <span className="flex items-center gap-2">{t32}Best {segmentNoun}</span>;
    $[72] = segmentNoun;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t31 || $[75] !== t33) {
    t34 = <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">{t31}{t33}</div>;
    $[74] = t31;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  const t35 = hoverTarget != null;
  let t36;
  if ($[77] !== tooltipRows) {
    t36 = <ChartTooltipPanel rows={tooltipRows} />;
    $[77] = tooltipRows;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t35 || $[80] !== t36 || $[81] !== tooltipPos.x || $[82] !== tooltipPos.y) {
    t37 = <FloatingTooltip open={t35} x={tooltipPos.x} y={tooltipPos.y}>{t36}</FloatingTooltip>;
    $[79] = t35;
    $[80] = t36;
    $[81] = tooltipPos.x;
    $[82] = tooltipPos.y;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t28 || $[85] !== t34 || $[86] !== t37 || $[87] !== t9) {
    t38 = <div className={t9}>{t28}{t29}{t34}{t37}</div>;
    $[84] = t28;
    $[85] = t34;
    $[86] = t37;
    $[87] = t9;
    $[88] = t38;
  } else {
    t38 = $[88];
  }
  return t38;
}